import { IoCloseSharp } from "react-icons/io5";
import axios from "../../config/axios";
import live_display_socket from "../../config/live_display_socket";
import { useTV } from "../../hooks/useTV";
import { useState, useRef, useEffect } from "react";
import useGlobalStore from "../../store/global.store";

const ParkChangeModal = ({ device }) => {
  const { isTv } = useTV();
  const [focusedButton, setFocusedButton] = useState(null);
  const firstParkButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const exitButtonRef = useRef(null);
  const buttonRefs = useRef({});
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focusIndex, setFocusIndex] = useState(0);

  useEffect(() => {
    console.log(focusedButton);
  }, [focusedButton]);

  // Use the zustand store for closing the modal
  const { setShowParkChange } = useGlobalStore();

  // Fetch parks data when component mounts
  useEffect(() => {
    setLoading(true);
    axios
      .get(`/parks/public/${device._id}`, { withCredentials: true })
      .then((response) => {
        setParks(response.data.parks);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching parks:", error);
        setLoading(false);
      });
  }, [device._id]);

  // Auto-focus first park button when component mounts in TV mode
  useEffect(() => {
    if (isTv && parks.length > 0 && firstParkButtonRef.current) {
      const firstPark = parks[0];
      setFocusedButton(`park-${firstPark.park_code}`);
      setFocusIndex(0);
      firstParkButtonRef.current.focus();
    }
  }, [isTv, parks]);

  // Set up keyboard navigation for TV mode
  useEffect(() => {
    if (!isTv) return;

    const handleKeyDown = (e) => {
      if (loading || parks.length === 0) return;

      // Close button has index -1, park buttons start at index 0
      if (e.key === "ArrowDown") {
        e.preventDefault();

        if (focusIndex === -1) {
          // Move from close button to first park
          setFocusIndex(0);
          const firstPark = parks[0];
          setFocusedButton(`park-${firstPark.park_code}`);
          if (buttonRefs.current[`park-${firstPark.park_code}`]) {
            buttonRefs.current[`park-${firstPark.park_code}`].focus();
            buttonRefs.current[`park-${firstPark.park_code}`].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else if (focusIndex === parks.length - 1) {
          // Move from last park to exit button
          setFocusIndex(parks.length);
          setFocusedButton("exit");
          if (exitButtonRef.current) {
            exitButtonRef.current.focus();
            exitButtonRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else if (focusIndex < parks.length - 1) {
          // Move to next park button
          const nextIndex = focusIndex + 1;
          const nextPark = parks[nextIndex];
          setFocusIndex(nextIndex);
          setFocusedButton(`park-${nextPark.park_code}`);
          if (buttonRefs.current[`park-${nextPark.park_code}`]) {
            buttonRefs.current[`park-${nextPark.park_code}`].focus();
            buttonRefs.current[`park-${nextPark.park_code}`].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();

        if (focusIndex === parks.length) {
          // Move from exit button to last park
          const lastIndex = parks.length - 1;
          const lastPark = parks[lastIndex];
          setFocusIndex(lastIndex);
          setFocusedButton(`park-${lastPark.park_code}`);
          if (buttonRefs.current[`park-${lastPark.park_code}`]) {
            buttonRefs.current[`park-${lastPark.park_code}`].focus();
            buttonRefs.current[`park-${lastPark.park_code}`].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else if (focusIndex === 0) {
          // Move from first park to close button
          setFocusIndex(-1);
          setFocusedButton("close");
          if (closeButtonRef.current) {
            closeButtonRef.current.focus();
            closeButtonRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else if (focusIndex > 0) {
          // Move to previous park button
          const prevIndex = focusIndex - 1;
          const prevPark = parks[prevIndex];
          setFocusIndex(prevIndex);
          setFocusedButton(`park-${prevPark.park_code}`);
          if (buttonRefs.current[`park-${prevPark.park_code}`]) {
            buttonRefs.current[`park-${prevPark.park_code}`].focus();
            buttonRefs.current[`park-${prevPark.park_code}`].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isTv, parks, loading, focusedButton, focusIndex]);

  const updateDeviceView = (park_code) => {
    if (isTv) document.getElementById("parkHopButton").focus();
    const view = "Ride__" + park_code + "_1";
    setShowParkChange(false);
    axios
      .patch(
        `/users/device/${device._id}`,
        { screen_view: view },
        { withCredentials: true }
      )
      .then(() => {
        live_display_socket.emit("remote_refresh", {
          device_id: device?._id,
        });
      })
      .catch((error) => {});
  };

  // Button style with focus handling
  const getButtonStyle = (buttonId, baseStyle) => {
    const focusedStyle =
      isTv && focusedButton === buttonId
        ? {
            border: "6px solid #3ADF3C",
            outline: "none",
          }
        : {};

    return {
      ...baseStyle,
      ...focusedStyle,
    };
  };

  // Base park button style
  const baseParkButtonStyle = {
    marginLeft: "10px",
    border: isTv ? "6px solid blue" : "2px solid blue",
    background: "transparent",
    color: "white",
    fontFamily: "Lato",
    fontSize: isTv ? "50px" : "40px",
    padding: isTv ? "10px 30px" : "0px 20px",
    borderRadius: "20px",
    fontWeight: "700",
    margin: "10px 0px",
    cursor: "pointer",
  };

  // Exit button style
  const exitButtonStyle = {
    marginLeft: "10px",
    border: isTv ? "6px solid red" : "2px solid red",
    background: "transparent",
    color: "red",
    fontFamily: "Lato",
    fontSize: isTv ? "50px" : "40px",
    padding: isTv ? "10px 30px" : "0px 20px",
    borderRadius: "20px",
    fontWeight: "700",
    margin: "20px 0px",
    cursor: "pointer",
    marginBottom: window.innerHeight,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "absolute",
        top: isTv ? "0%" : "0%",
        backgroundColor: "white",
        width: "100vw",
        height: "100vh",
        zIndex: 50,
        opacity: "80%",
      }}
    >
      {!isTv && (
        <button
          tabIndex={0}
          ref={closeButtonRef}
          onFocus={() => {
            setFocusedButton("close");
            setFocusIndex(-1);
          }}
          onBlur={() => setFocusedButton(null)}
          onClick={() => {
            setShowParkChange(false);
            if (isTv) document.getElementById("parkHopButton").focus();
          }}
          style={{
            position: "absolute",
            top: "5%",
            right: "3%",
            background: "transparent",
            border:
              isTv && focusedButton === "close"
                ? "6px solid #3ADF3C"
                : "6px solid transparent",
            borderRadius: "50%",
            padding: "5px",
            cursor: "pointer",
            outline: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IoCloseSharp size={isTv ? 100 : 60} />
        </button>
      )}

      {loading ? (
        <p style={{ fontSize: isTv ? "60px" : "30px", color: "blue" }}>
          Loading parks...
        </p>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflowY: "scroll",
            height: "90vh",
            width: "80vw",
            marginTop: isTv ? "0px" : "110px",
          }}
        >
          {parks.map((park, index) => {
            const buttonId = `park-${park.park_code}`;
            return (
              <button
                key={index}
                ref={(el) => {
                  if (index === 0) firstParkButtonRef.current = el;
                  buttonRefs.current[buttonId] = el;
                }}
                tabIndex={0}
                onFocus={() => {
                  setFocusedButton(buttonId);
                  setFocusIndex(index);
                }}
                onBlur={() => setFocusedButton(null)}
                style={getButtonStyle(buttonId, baseParkButtonStyle)}
                onClick={() => updateDeviceView(park.park_code)}
              >
                <span
                  style={{
                    fontFamily: "Lato",
                    fontWeight: "700",
                    color: "blue",
                  }}
                >
                  {park.park_name}
                </span>
              </button>
            );
          })}

          {isTv && !loading && (
            <button
              tabIndex={0}
              ref={exitButtonRef}
              onFocus={() => {
                setFocusedButton("exit");
                setFocusIndex(parks.length);
              }}
              onBlur={() => setFocusedButton(null)}
              onClick={() => {
                setShowParkChange(false);
                document.getElementById("parkHopButton").focus();
              }}
              style={getButtonStyle("exit", exitButtonStyle)}
            >
              Exit
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ParkChangeModal;
